import { useThemeStore } from '../../stores/themeStore';

export interface BilldaLogoProps {
  /**
   * Variant of the logo to display
   * - 'primary': Yellow B + dark text (for light backgrounds)
   * - 'secondary': Yellow B + white text (for dark backgrounds)
   * - 'icon': Just the yellow B icon
   * - 'auto': Automatically choose based on theme mode
   */
  variant?: 'primary' | 'secondary' | 'icon' | 'auto';

  /**
   * Width of the logo (height scales proportionally)
   */
  width?: number | string;

  /**
   * Alt text for accessibility
   */
  alt?: string;

  /**
   * Additional CSS class names
   */
  className?: string;
}

// Byte-identical copies of shared-ui's assets/logos served from public/
const LOGO_SRC = {
  primary: '/billda-logo.svg',
  secondary: '/billda-logo-light.svg',
  icon: '/favicon.svg',
} as const;

/**
 * Billda Logo Component (DS fork of shared-ui BilldaLogo)
 *
 * Renders the Billda logo with automatic light/dark mode support, reading
 * the mode from the portal theme store instead of MUI useTheme.
 */
export const BilldaLogo = ({
  variant = 'auto',
  width = 160,
  alt = 'Billda',
  className = '',
}: BilldaLogoProps) => {
  const mode = useThemeStore(state => state.mode);

  const logoVariant =
    variant === 'auto' ? (mode === 'dark' ? 'secondary' : 'primary') : variant;
  const src =
    logoVariant === 'icon'
      ? LOGO_SRC.icon
      : logoVariant === 'secondary'
        ? LOGO_SRC.secondary
        : LOGO_SRC.primary;
  const numericWidth = typeof width === 'number' ? width : undefined;
  const intrinsicHeight =
    numericWidth == null
      ? undefined
      : logoVariant === 'icon'
        ? numericWidth
        : Math.round((numericWidth * 400) / 1251);

  return (
    <img
      src={src}
      alt={alt}
      width={numericWidth}
      height={intrinsicHeight}
      className={className}
      style={{
        width: typeof width === 'number' ? `${width}px` : width,
        height: 'auto',
        display: 'block',
      }}
    />
  );
};
